import { useContext, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Link as RouterLink } from 'react-router-dom'

import clsx from 'clsx'

import CloseIcon from '@mui/icons-material/Close'
import { Alert, Button, IconButton } from '@mui/material'
import Collapse from '@mui/material/Collapse'
import Skeleton from '@mui/material/Skeleton'
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles'

import FadeAnimationDiv from 'components/common/Animations'
import { ModalFormsContext } from 'components/modals'
import { RootState } from 'redux/store'
import { startAlertTransaction } from 'redux/transactions/transactions-actions'
import { useTheme } from 'themes'
import { toLocaleStringWithCurrency } from 'utils/helperNumbersFormating'

import { newFormThemeOverrides } from '../styles'
import { SeverityMap } from '../utils'

const NotificationAlertComponent = () => {
    const { formTheme } = useTheme({ formTheme: newFormThemeOverrides })
    const { t } = useTranslation(['override', 'translations', 'keys'])
    const { modalValues } = useContext(ModalFormsContext)
    const dispatch = useDispatch()
    const { notifications, notificationAlert } = useSelector((state: RootState) => state.alert)
    const { alertTransaction, loadingAlert } = useSelector((state: RootState) => state.transactions)

    const [open, setOpen] = useState(true)
    const [pendingPayment, setPendingPayment] = useState({
        pendingAmount: null,
        pendingCurrency: null,
        pendingAmountWithCurrency: null,
    })
    const { pendingAmountWithCurrency } = pendingPayment

    const [alertDetails, setAlertDetails] = useState({
        alertNo: '',
        alertType: false,
        alertMessage: '',
        alertSeverity: '',
        alertDismiss: false,
        alertTransferNo: null,
        alertDocumentType: '',
        alertAmount: '',
        alertAmountCurrency: '',
        alertAmountWithCurrency: '',
        alertIdentityStatus: '',
        alertAccountIdentifier: '',
    })
    const {
        alertType,
        alertSeverity,
        alertDismiss,
        alertTransferNo,
        alertDocumentType,
        alertAmountWithCurrency,
        alertIdentityStatus,
        alertAccountIdentifier,
    }: { [key: string]: any } = alertDetails

    useEffect(() => {
        if (alertTransaction) {
            setPendingPayment({
                pendingCurrency: alertTransaction?.debit?.value?.currencyCode,
                pendingAmount: alertTransaction?.debit?.value?.value,
                pendingAmountWithCurrency: toLocaleStringWithCurrency(
                    Math.abs(alertTransaction?.debit?.amount?.value),
                    alertTransaction?.debit?.value?.currencyCode
                ),
            })
        }
    }, [alertTransaction])

    useEffect(() => {
        if (notifications?.pendingAlertsRequests) {
            const filtered = notifications.pendingAlertsRequests.find(t =>
                [
                    'DepositRequest',
                    'Payment',
                    'Document',
                    'Identity',
                    'GlobalPassDocumentSubmissionRequired',
                    'FeeBasketNotAssigned',
                    'KycReviewInProgress',
                    'IdentityStatus',
                    'AmlInvestigation',
                    'IdentityDocumentExpired',
                    'IdentityDocumentExpiring',
                    'IdentityDepositLimitNearThreshold',
                    'IdentityDepositLimitOverThreshold',
                ].includes(t.type)
            )

            setAlertDetails({
                alertNo: filtered?.no,
                alertType: filtered?.type || false,
                alertMessage: filtered?.message,
                alertSeverity: filtered?.severity,
                alertDismiss: filtered?.canDismiss,
                alertTransferNo: filtered?.transferNo || null,
                alertDocumentType: filtered?.documentType,
                alertAmount: filtered?.amount?.amount,
                alertAmountCurrency: filtered?.amount?.currencyCode,
                alertAmountWithCurrency: toLocaleStringWithCurrency(filtered?.amount?.amount, filtered?.amount?.currencyCode || 'EUR'),
                alertIdentityStatus: filtered?.identityStatus,
                alertAccountIdentifier: filtered?.accountId,
            })
        }
    }, [notifications])

    useEffect(() => {
        alertTransferNo && dispatch(startAlertTransaction({ no: alertTransferNo }))
    }, [alertTransferNo])

    const Document = () => (
        <div className="message">
            <span className="textWrap">
                <span className="type">{t(`Alert.${alertSeverity}`)}:</span>
                <span className="text">
                    <Trans i18nKey={`keys:alerts.${alertDocumentType}`} defaults={t('keys:alerts.fallBack')}></Trans>
                </span>
            </span>
            <RouterLink to="/settings/documents" style={{ textDecoration: 'none' }}>
                <Button className="button" variant="outlined">
                    {t('Alert.NotificationAlertComponent.ProvideDocs')}{' '}
                </Button>
            </RouterLink>
        </div>
    )

    const Payment = () => (
        <div className="message">
            <span className="textWrap">
                <span className="type">{t(`Alert.${alertSeverity}`)}:</span>
                <span className="text">
                    <Trans i18nKey="Alert.NotificationAlertComponent.YouHavePendingAmount">
                        You have <strong>{{ pendingAmountWithCurrency }}</strong> amount pending debt bond.
                    </Trans>
                </span>
            </span>
            <div
                onClick={() =>
                    modalValues.handleModalOpen({
                        transferNo: alertTransferNo,
                        modal: 'PayFormModal',
                    })
                }
            >
                <Button variant="outlined" data-sta-id="pending-debt-bond-pay-now">
                    {t('Alert.NotificationAlertComponent.PayNow')}
                </Button>
            </div>
        </div>
    )

    const DepositRequest = () => (
        <div className="message">
            <span className="textWrap">
                <span className="type">{t(`Alert.${alertSeverity}`)}:</span>
                <span data-sta-id="business-account-span-pending-payment" className="text">
                    <Trans i18nKey="Alert.NotificationAlertComponent.YouHavePendingIDFAmount">
                        You have <strong>{{ alertAmountWithCurrency }}</strong> amount pending payment for Business account activation.
                    </Trans>
                </span>
            </span>
            <div
                onClick={() =>
                    modalValues.handleModalOpen({
                        identifier: alertAccountIdentifier,
                        currentTab: 'AddMoney',
                        modal: 'AssetsFormModal',
                    })
                }
            >
                <Button variant="outlined" data-sta-id="business-account-pending-payment-add-money">
                    {t('Alert.NotificationAlertComponent.AddMoney')}
                </Button>
            </div>
        </div>
    )

    const Identity = () => (
        <div className="message">
            <span className="textWrap">
                <span className="type">{t(`Alert.${alertSeverity}`)}:</span>
                <span className="text">{t('CompleteVerification.CompleteKYCFunctionality')}</span>
            </span>
            <RouterLink to="/verification" style={{ textDecoration: 'none' }}>
                <Button className="button" variant="outlined">
                    {t('CompleteVerification.ButtonDone')}
                </Button>
            </RouterLink>
        </div>
    )

    const IdentityStatus = () => {
        const IdentityStatusMessage = () => {
            if (alertIdentityStatus === 'Suspended') {
                return (
                    <div className="message">
                        <span className="textWrap">
                            <span className="type">{t(`Alert.${alertSeverity}`)}:</span>
                            <span className="text">{t('Alert.NotificationAlertComponent.Suspended')}</span>
                        </span>
                        <RouterLink to="/settings/support" style={{ textDecoration: 'none' }}>
                            <Button className="button" variant="outlined">
                                {' '}
                                {t('Alert.NotificationAlertComponent.GoToSupport')}
                            </Button>
                        </RouterLink>
                    </div>
                )
            }

            if (alertIdentityStatus === 'Deactivated') {
                return (
                    <div className="message">
                        <span className="textWrap">
                            <span className="type">{t(`Alert.${alertSeverity}`)}:</span>
                            <span className="text">{t('Alert.NotificationAlertComponent.Deactivated')}</span>
                        </span>
                    </div>
                )
            }

            return (
                <div className="message">
                    <span className="textWrap">
                        <span className="type">{t(`Alert.${alertSeverity}`)}:</span>
                        <span className="text">{t('Alert.NotificationAlertComponent.PleaseContactSupport')}</span>
                    </span>
                    <RouterLink to="/settings/support" style={{ textDecoration: 'none' }}>
                        <Button className="button" variant="outlined">
                            {' '}
                            {t('Alert.NotificationAlertComponent.GoToSupport')}
                        </Button>
                    </RouterLink>
                </div>
            )
        }

        return <IdentityStatusMessage />
    }

    const FeeBasket = () => (
        <div className="message">
            <span className="textWrap">
                <span className="text">{t('ServicePlans.ChooseYourProductPlanBeforeContinuing')}.</span>
            </span>
            <RouterLink to="/service-plans" style={{ textDecoration: 'none' }}>
                <Button className="button" variant="outlined">
                    {' '}
                    {t('ServicePlans.SelectProduct')}{' '}
                </Button>
            </RouterLink>
        </div>
    )

    const KycReviewInProgress = () => (
        <div className="message">
            <span className="textWrap">
                <span className="type">{t(`Alert.${alertSeverity}`)}:</span>
                <span className="text">{t('Alert.NotificationAlertComponent.InReview')}</span>
            </span>
        </div>
    )

    const AmlInvestigation = () => (
        <div className="message">
            <span className="textWrap">
                <span className="type">{t(`Alert.${alertSeverity}`)}:</span>
                <span className="text">
                    <Trans i18nKey="Alert.NotificationAlertComponent.AmlInvestigation">
                        Your account is limited due to AML verification. It may take <strong>up to 5 business days</strong> for verification
                        to be completed.
                    </Trans>
                </span>
            </span>
        </div>
    )

    const IdentityDocumentExpired = () => (
        <div className="message">
            <span className="textWrap">
                <span className="type">{t(`Alert.${alertSeverity}`)}:</span>
                <span className="text">{t('Alert.NotificationAlertComponent.IdentityDocumentExpired')}</span>
            </span>
            <RouterLink to="/verification" style={{ textDecoration: 'none' }}>
                <Button className="button" variant="outlined">
                    {t('Alert.CompleteKYC')}
                </Button>
            </RouterLink>
        </div>
    )

    const IdentityDocumentExpiring = () => (
        <div className="message">
            <span className="textWrap">
                <span className="type">{t(`Alert.${alertSeverity}`)}:</span>
                <span className="text">{t('Alert.NotificationAlertComponent.IdentityDocumentExpiring')}</span>
            </span>
            <RouterLink to="/verification" style={{ textDecoration: 'none' }}>
                <Button className="button" variant="outlined">
                    {t('Alert.CompleteKYC')}
                </Button>
            </RouterLink>
        </div>
    )

    const IdentityDepositLimitNearThreshold = () => (
        <div className="message">
            <span className="textWrap">
                <span className="type">{t(`Alert.${alertSeverity}`)}:</span>
                <span className="text">{t('Alert.NotificationAlertComponent.IdentityDepositLimitNearThreshold')}</span>
            </span>
            <RouterLink to="/settings/profile" style={{ textDecoration: 'none' }}>
                <Button className="button" variant="outlined">
                    {t('Alert.NotificationAlertComponent.GoToProfile')}
                </Button>
            </RouterLink>
        </div>
    )

    const IdentityDepositLimitOverThreshold = () => (
        <div className="message">
            <span className="textWrap">
                <span className="type">{t(`Alert.${alertSeverity}`)}:</span>
                <span className="text">{t('Alert.NotificationAlertComponent.IdentityDepositLimitOverThreshold')}</span>
            </span>
            <RouterLink to="/settings/documents" style={{ textDecoration: 'none' }}>
                <Button className="button" variant="outlined">
                    {t('Alert.NotificationAlertComponent.ProvideDocs')}
                </Button>
            </RouterLink>
        </div>
    )

    const Components = {
        DepositRequest: <DepositRequest />,
        Payment: <Payment />,
        Identity: <Identity />,
        Document: <Document />,
        FeeBasketNotAssigned: <FeeBasket />,
        KycReviewInProgress: <KycReviewInProgress />,
        IdentityStatus: <IdentityStatus />,
        AmlInvestigation: <AmlInvestigation />,
        IdentityDocumentExpired: <IdentityDocumentExpired />,
        IdentityDocumentExpiring: <IdentityDocumentExpiring />,
        IdentityDepositLimitNearThreshold: <IdentityDepositLimitNearThreshold />,
        IdentityDepositLimitOverThreshold: <IdentityDepositLimitOverThreshold />,
    }

    if (loadingAlert) {
        return (
            <FadeAnimationDiv>
                <StyledEngineProvider injectFirst>
                    <ThemeProvider theme={formTheme}>
                        <Alert icon={false}>
                            <div className="iconBox">
                                <Skeleton variant="rectangular" height={60} width={50} />
                            </div>
                            <div className="message">
                                <span className="text">
                                    <Skeleton variant="text" width={500} height={36} />
                                </span>
                            </div>
                        </Alert>
                    </ThemeProvider>
                </StyledEngineProvider>
            </FadeAnimationDiv>
        )
    }

    if (notificationAlert && alertType) {
        return (
            <FadeAnimationDiv>
                <StyledEngineProvider injectFirst>
                    <ThemeProvider theme={formTheme}>
                        <Collapse in={open} timeout={500}>
                            <Alert
                                icon={false}
                                className={clsx(alertSeverity, 'notification')}
                                action={
                                    alertDismiss && (
                                        <IconButton
                                            className="alertDismiss"
                                            aria-label="close"
                                            color="inherit"
                                            size="small"
                                            onClick={() => {
                                                setOpen(false)
                                            }}
                                        >
                                            <CloseIcon fontSize="inherit" />
                                        </IconButton>
                                    )
                                }
                            >
                                <div className="iconBox">
                                    <img className="iconImg" src={SeverityMap[alertSeverity]} alt={alertSeverity} />
                                </div>
                                {Components[alertType]}
                            </Alert>
                        </Collapse>
                    </ThemeProvider>
                </StyledEngineProvider>
            </FadeAnimationDiv>
        )
    }

    return <></>
}

export default NotificationAlertComponent
